import axios, { AxiosError, AxiosResponse, Cancel } from 'axios';
import { toast } from 'react-toastify';
import { history } from '../..';
import { CancelMarketingDTO, DeleteIntroDTO, DeleteRepairShopDTO, DeleteRestingPlaceDTO, DetailMarketingDTO, IntroCardDTO, IntroRecommendationDTO, IntroVideoDTO, MarketingDTO, ProductDTO, RepairShopDTO, RepairShopDetailDTO, RestingPlaceDTO, UpdateProductOrderDTO, UpdateRetrofitDTO } from '../models/models';
import { PaginatedResult } from '../models/pagination';
import { ServerError } from '../models/serverError';
import { CodeInfo, User, UserFormValues, UserSearchListItem } from '../models/user';
import { store } from '../stores/store';


axios.defaults.baseURL = process.env.REACT_APP_API_URL + 'api';

axios.interceptors.request.use(config => {
    const token = store.commonStore.token;
    if (token) config.headers.Authorization = `Bearer ${token}`
    config.withCredentials = true;
    return config;
})


axios.interceptors.response.use(async response => {
    const pagination = response.headers['pagination'];
    if (pagination) {
        response.data = new PaginatedResult(response.data, JSON.parse(pagination));
        return response as AxiosResponse<PaginatedResult<any>>
    }
    return response;
}, (error: AxiosError) => {
    if (error.response) {
        const { data, status, config } = error.response!;
        switch (status) {
            case 400:
                if (config.method === 'get' && data.errors.hasOwnProperty('id')) {
                    history.push('/');
                    toast.error(data);
                }
                if (data.errors) {
                    const modalStateErrors = [];
                    for (const key in data.errors) {
                        if (data.errors[key]) {
                            modalStateErrors.push(data.errors[key])
                        }
                    }
                    toast.error(modalStateErrors.flat().toString());
                    throw modalStateErrors.flat();
                } else {
                    toast.error(data);
                }
                break;
            case 429:
                toast.error('Ardarda çok fazla istek yapıldı. Lütfen daha sonra tekrar deneyin.');
                break;
            case 401:
                store.userStore.logout();
                history.push('/');
                break;
            case 404:
                history.push('/not-found');
                break;
            case 500:
                store.commonStore.setServerError(data);
                history.push('/server-error');
                break;
            default:
                toast.error("Bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.");
                break;
        }
    }
    else {
        let detail: string = '';


        if (error.message.trim().toUpperCase() === "NETWORK ERROR") {
            detail = 'Ağ bağlantısında problem oluştu!'
        }
        else {
            detail = error.stack ? error.stack : '';

        }

        const errM: ServerError = {
            statusCode: 99,
            message: error.message,
            details: detail
        };

        store.commonStore.setServerError(errM);
        history.push('/server-error');
        toast.error(error.message);
    }
    return Promise.reject(error);
})

const responseBody = <T>(response: AxiosResponse<T>) => response.data;
const responseStatus = <T>(response: AxiosResponse<T>) => response.status;

const requests = {

    get: <T>(url: string) => axios.get<T>(url).then(responseBody),
    post: <T>(url: string, body: {}) => axios.post<T>(url, body).then(responseBody),
    postwStatus: <T>(url: string, body: {}) => axios.post<T>(url, body).then(responseStatus),
    put: <T>(url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
    del: <T>(url: string) => axios.delete<T>(url).then(responseBody),
    download: <BLob>(url: string, body: {}, responseType: any) => axios.post<Blob>(url, body).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'file.csv');
        document.body.appendChild(link);
        link.click();
    })


}

const download = {
    download: (postUrl: string, postId: string, filename: string) => axios.request({
        method: 'POST', url: postUrl,
        data: { id: postId }, responseType: 'blob'
    })
        .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename + '.xlsx');
            document.body.appendChild(link);
            link.click();
        })
}

const IntroCard = {
    listCards: () => requests.get<IntroCardDTO[]>('/IntroCard/ListIntroCards'),
    detail: (accountId: string) => requests.get<IntroCardDTO>(`/IntroCard/IntroCardDetail/${accountId}`),
    createCard: (card: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/IntroCard/CreateIntroCard', card, config);
    },
    updateCard: (card: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/IntroCard/UpdateIntroCard', card, config);
    },


}

const IntroVideo = {
    listVideos: () => requests.get<IntroVideoDTO[]>('/IntroVideo/ListVideos'),
    detail: (accountId: string) => requests.get<IntroVideoDTO>(`/IntroVideo/IntroVideoDetail/${accountId}`),
    createVideo: (video: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/IntroVideo/CreateIntroVideo', video, config);
    },
    updateVideo: (video: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/IntroVideo/UpdateIntroVideo', video, config);
    },
}

const IntroRecommendation = {
    listRecommendations: () => requests.get<IntroRecommendationDTO[]>('/IntroRecommendation/ListRecommendation'),
    detail: (accountId: string) => requests.get<IntroRecommendationDTO>(`/IntroRecommendation/IntroRecommendationDetail/${accountId}`),
    createRecommendation: (recommendation: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/IntroRecommendation/CreateIntroRecommendation', recommendation, config);
    },
    updateRecommendation: (recommendation: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/IntroRecommendation/UpdateIntroRecommendation', recommendation, config);
    },
}

const Intros = {
    deleteIntro: (selectedIntro: DeleteIntroDTO) => requests.postwStatus<void>('/Intro/DeleteIntro', selectedIntro),

}

const RestingPlaces = {
    list: () => requests.get<RestingPlaceDTO[]>('/RestingPlace/ListRestingPlaces'),
    delete: (selectedRestinPlace: DeleteRestingPlaceDTO) => requests.postwStatus<void>('/RestingPlace/DeleteRestingPlace', selectedRestinPlace),
    detail: (accountId: string) => requests.get<RestingPlaceDTO>(`/RestingPlace/DetailRestingPlace/${accountId}`),
    getRPServices: () => requests.get<RestingPlaceDTO[]>('/RestingPlace/ListRestingPlaces'),
    create: (restingPlace: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/RestingPlace/CreateRestingPlace', restingPlace, config);
    },

    update: (restingPlace: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/RestingPlace/UpdateRestingPlace', restingPlace, config);
    },
}

const RepairShops = {
    list: () => requests.get<RepairShopDTO[]>('/RepairShop/list'),
    delete: (selectedRestinPlace: CancelMarketingDTO) => requests.postwStatus<void>('/RepairShop/delete', selectedRestinPlace),
    detail: (accountId: string) => requests.get<RepairShopDetailDTO>(`/RepairShop/detail/${accountId}`),
    create: (repairShop: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/RepairShop/create', repairShop, config);
    },

    update: (repairShop: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/RepairShop/update', repairShop, config);
    },
}


const Story = {
    list: () => requests.get<MarketingDTO[]>('/story/list'),
    detail: (accountId: string) => requests.get<DetailMarketingDTO>(`/story/detail/${accountId}`),
    cancelStory: (story: CancelMarketingDTO) => requests.postwStatus('/story/cancel', story),
    exportStory: (story: CancelMarketingDTO) => requests.download('/story/export', story, new Blob),
    createStory: (story: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/story/create', story, config);
    },
}

const Campaign = {
    list: () => requests.get<MarketingDTO[]>('/campaign/list'),
    detail: (accountId: string) => requests.get<DetailMarketingDTO>(`/campaign/detail/${accountId}`),
    cancelCampaign: (campaign: CancelMarketingDTO) => requests.postwStatus('/campaign/cancel', campaign),
    createCampaign: (campaign: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/campaign/create', campaign, config);
    },
}
const CompanyNew = {
    list: () => requests.get<MarketingDTO[]>('/company-new/list'),
    detail: (accountId: string) => requests.get<DetailMarketingDTO>(`/company-new/detail/${accountId}`),
    cancelCompanyNew: (companyNew: CancelMarketingDTO) => requests.postwStatus('/company-new/cancel', companyNew),
    createCompanyNew: (companyNew: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/company-new/create', companyNew, config);
    },
}

const Product = {
    list: () => requests.get<ProductDTO[]>('/Product/list'),
    detail: (accountId: string) => requests.get<ProductDTO>(`/Product/detail/${accountId}`),
    cancelProduct: (product: CancelMarketingDTO) => requests.postwStatus('/Product/cancel', product),
    createProduct: (product: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/Product/create', product, config);
    },

    updateRetrofit: (retrofit: FormData) => {
        const config = {
            headers: {
                'content-type': 'multipart/form-data',
            },
        };
        return axios.post('/Product/update-retrofit', retrofit, config);
    },
    updateRetrofit2: (retrofit: UpdateRetrofitDTO) => requests.postwStatus('/Product/update-retrofit', retrofit),
    updateOrder: (product: UpdateProductOrderDTO) => requests.postwStatus('/Product/update-order', product),
}

const Account = {
    current: () => requests.get<User>('/Account'),
    login: (user: UserFormValues) => requests.post<User>('/Account/login', user),
    openIdLogin: (code: CodeInfo) => requests.post<User>('/Account/OpenIdLogin', code),
    register: (user: UserFormValues) => requests.post<User>('/Account/register', user),
    refreshToken: () => requests.post<User>("/Account/refreshToken", {}),
}

const Users = {
    get: (username: string) => requests.get<UserSearchListItem[]>(`/users/${username}`),
}



const agent = {
    Account,
    Users,
    IntroCard,
    IntroVideo,
    IntroRecommendation,
    Intros,
    RepairShops,
    RestingPlaces,
    Story,
    Campaign,
    Product,
    download,
    CompanyNew,
}

export default agent;